import { useEffect, useRef, useState } from "react";
import AnchoredPopover from "./AnchoredPopover.jsx";
import { clientSurfaceLabel } from "../lib/clientSurfaceIdentity.js";
import { evidenceProvenanceLines } from "../lib/sourceEvidence.js";
import { inferenceProviderLabel } from "../lib/inferenceServiceIdentity.js";

export default function TraceEvidenceButton({ tooling, label = "Evidence", hover = false }) {
  const anchor = useRef(null);
  const closeButton = useRef(null);
  const content = useRef(null);
  const closeTimer = useRef(null);
  const hovered = useRef(false);
  const restoringFocus = useRef(false);
  const [open, setOpen] = useState(false);
  const [limit, setLimit] = useState(8);
  useEffect(() => { if (open && !hover) closeButton.current?.focus(); }, [open, hover]);
  useEffect(() => () => clearTimeout(closeTimer.current), []);
  const keepOpen = () => clearTimeout(closeTimer.current);
  const close = (restore = true) => {
    keepOpen();
    setOpen(false);
    if (restore) {
      restoringFocus.current = true;
      anchor.current?.focus();
      restoringFocus.current = false;
    }
  };
  const show = () => { keepOpen(); if (!restoringFocus.current) setOpen(true); };
  const leave = () => {
    keepOpen();
    closeTimer.current = setTimeout(() => {
      if (!hovered.current && document.activeElement !== anchor.current && !content.current?.contains(document.activeElement)) close(false);
    }, 120);
  };
  const enterHover = () => { hovered.current = true; show(); };
  const leaveHover = () => { hovered.current = false; leave(); };
  const observations = tooling.observations || [];
  const triggerProps = { ref: anchor, "aria-haspopup": "dialog", "aria-expanded": open };
  return <>
    {hover ? <span {...triggerProps} role="button" tabIndex={0}
      className="block max-w-full cursor-help truncate text-[11px] leading-[1.08] text-[color:var(--ink)] underline decoration-dotted underline-offset-2 focus-visible:outline focus-visible:outline-[color:var(--green)]"
      onMouseEnter={enterHover} onMouseLeave={leaveHover} onFocus={show} onBlur={leave}
      onClick={event => { event.stopPropagation(); anchor.current?.focus(); show(); }}
      onKeyDown={event => {
        if (event.key === "Enter" || event.key === " ") { event.preventDefault(); event.stopPropagation(); show(); }
      }}>{label}</span> : <button {...triggerProps} type="button"
      className="cursor-pointer text-[11px] text-[color:var(--ink)] underline decoration-dotted underline-offset-2"
      onClick={event => { event.stopPropagation(); setLimit(8); setOpen(value => !value); }}>{label}</button>}
    {open && <AnchoredPopover anchor={anchor} onClose={close} align="end" label="Client and inference evidence"
      onMouseEnter={hover ? enterHover : undefined} onMouseLeave={hover ? leaveHover : undefined}
      className="w-[480px] max-w-[calc(100vw-16px)] overflow-y-auto whitespace-normal p-[12px] text-left text-[12px]">
      <div ref={content} onClick={event => event.stopPropagation()} onKeyDown={event => { if (event.key !== "Escape") event.stopPropagation(); }}
        onFocusCapture={hover ? keepOpen : undefined} onBlurCapture={hover ? leave : undefined}>
      <div className="flex items-center justify-between gap-[12px]">
        <strong>Client and inference evidence</strong>
        <button ref={closeButton} type="button" onClick={() => close()} className="cursor-pointer underline">Close</button>
      </div>
      <p className="mt-[10px]">Client: {tooling.label || "Client surface unavailable"}</p>
      <p>Intelligence Provider: {tooling.inferenceProvider || "unknown"}</p>
      {tooling.truncated && <p className="mt-[8px]">Some evidence was not returned. These values are incomplete.</p>}
      {(tooling.qualifiers || []).map(item => <p key={item.key}>{item.source}: {item.detail}</p>)}
      {!observations.length && tooling.evidence.map((item, i) => <p key={i} className="mt-[8px] break-all">{item.source}: {item.detail}</p>)}
      {!hover && observations.slice(0, limit).map((observation, i) => <section key={i} className="mt-[12px] border-t border-[color:var(--line)] pt-[8px]">
        <h3 className="break-all font-medium">Observation {observation.sourceEventId || i + 1}</h3>
        {observation.conflict && <p>Client evidence conflicts within this observation.</p>}
        {!observation.clientEvidence.length && <p>No client product is established by these facts.</p>}
        {observation.clientEvidence.map((candidate, j) => <div key={j} className="mt-[6px]">
          <strong>{clientSurfaceLabel(candidate.identity)}</strong>
          {evidenceProvenanceLines([candidate]).map((line, k) => <p key={k} className="break-all text-[color:var(--muted)]">{line}</p>)}
        </div>)}
        <p className="mt-[6px]">Intelligence Provider: {observation.providerConflict ? "conflicting evidence" : inferenceProviderLabel(observation.provider) || "unknown"}</p>
        {(observation.providerEvidence || []).map((claim, j) => <div key={j} className="mt-[4px]">
          <strong>{inferenceProviderLabel(claim.value) || "Unrecognized value"}</strong>
          {evidenceProvenanceLines([claim]).map((line, k) => <p key={k} className="break-all text-[color:var(--muted)]">{line}</p>)}
        </div>)}
        <details className="mt-[6px]"><summary className="cursor-pointer">Source fields ({observation.rawEvidence.length})</summary>
          {observation.rawEvidence.map((fact, j) => <p key={j} className="mt-[4px] break-all text-[color:var(--muted)]">{evidenceProvenanceLines([{ fact }]).join("; ")}</p>)}
        </details>
      </section>)}
      {!hover && observations.length > limit && <button type="button" className="mt-[10px] cursor-pointer underline" onClick={() => setLimit(value => value + 8)}>Show more observations ({observations.length - limit} remaining)</button>}
      </div>
    </AnchoredPopover>}
  </>;
}
